import { nowUTC } from "@homebound/activesupport";
import { ContentHeader, HeaderAction, Palette, useModal } from "@homebound/beam";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { cycleTrackingAction } from "src/components/CycleTrackingAction";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import {
  AiProposalStatus,
  FeatureFlagType,
  PlanPackageIncomingSlotStatus,
  PotentialOperation2,
  SavePlanPackageInput,
} from "src/generated/graphqlTypes";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { GenerateTakeoffModal } from "src/routes/libraries/plan-package-v2/components/takeoffs/GenerateTakeoffModal";
import { addLineMenuItems } from "src/routes/libraries/plan-package-v2/components/takeoffs/side-panel/lineTypes";
import { useTakeoffsSidePanel } from "src/routes/libraries/plan-package-v2/components/takeoffs/side-panel/useTakeoffsSidePanel";
import { useImportItems } from "src/routes/libraries/plan-package-v2/components/takeoffs/useImportItems";
import {
  PlanPackagePageV2_PlanPackageFragment,
  usePlanPackagePageV2MetadataQuery,
} from "src/routes/libraries/plan-package-v2/PlanPackagePageV2.generated";
import { LineType } from "src/routes/libraries/plan-package/takeoffs/components/AddItems/utils";
import { useSavePlanPackageTakeoffsCycleMutation } from "src/routes/libraries/plan-package/takeoffs/PlanPackageTakeoffPage.generated";
import { useTakeoffsStore } from "src/routes/libraries/plan-package/takeoffs/TakeoffsManagerContext";
import { createPlanPackageIncomingSlotReviewUrl } from "src/RouteUrls";
import {
  planPackageElevationsRoute,
  planPackageLocationsRoute,
  planPackageOptionsRoute,
  planPackageProgramDataRoute,
} from "src/routing/domains/libraries/plan-packages/planPackagesPaths";
import { pluralize } from "src/utils";

export function TakeoffsTabHeader() {
  const planPackage = useTakeoffsStore((state) => state.planPackage);
  const designerModifiedSlotsEnabled = useFeatureFlag(FeatureFlagType.DesignerModifiedSlots);
  const planPackagesAiEnabled = useFeatureFlag(FeatureFlagType.PlanPackagesAi);
  const { id, version, incomingSlots, takeoffsStartedAt, takeoffsCompletedAt, canEditLineItems } = planPackage;
  const { openModal } = useModal();
  const navigate = useNavigate();
  const importItems = useImportItems();
  const { openAdd } = useTakeoffsSidePanel();
  const { data: metadata } = usePlanPackagePageV2MetadataQuery({
    // The page shell's query and variables. `cache-first` reads its result rather than re-running it on every
    // mount, and the review tabs' mutations keep the proposal fields current.
    variables: { id, versionId: version.id },
    fetchPolicy: "cache-first",
    skip: !planPackagesAiEnabled,
  });

  const [saveTakeoffsCycle] = useSavePlanPackageTakeoffsCycleMutation();
  const saveCycle = useCallback(
    (input: Pick<SavePlanPackageInput, "takeoffsStartedAt" | "takeoffsCompletedAt">) =>
      saveTakeoffsCycle({ variables: { input: { id, ...input } } }),
    [saveTakeoffsCycle, id],
  );

  const pendingSlotCount = incomingSlots.count((s) => s.status.code === PlanPackageIncomingSlotStatus.Pending);
  const reviewSlotsAction: HeaderAction = {
    label: `${pendingSlotCount} ${pluralize(pendingSlotCount, "slot")} to review`,
    variant: "caution",
    icon: "todo",
    onClick: createPlanPackageIncomingSlotReviewUrl(id, version.id),
  };
  const aiProposal = metadata?.planPackage.aiProposal;
  const suggestionsRoute = pendingSuggestionsRoute(aiProposal);
  const generateTakeoffsAction: HeaderAction = {
    label: "Generate Takeoffs",
    variant: "secondary",
    disabled:
      disableBasedOnPotentialOperation(canEditLineItems) || (!suggestionsRoute && openProposalReason(aiProposal)),
    onClick: () =>
      suggestionsRoute
        ? openModal({
            content: (
              <ConfirmationModal
                title="This Plan has Pending AI Suggestions"
                icon={{ icon: "error", color: Palette.Orange600 }}
                confirmationMessage="You have AI-generated changes to the program data, locations, or options that haven't been accepted yet. Before we can generate lines of scope, save or reject outstanding AI suggestions."
                label="Review Suggestions"
                onConfirmAction={() =>
                  navigate(suggestionsRoute.url({ planPackageId: id, planPackageVersionId: version.id }))
                }
              />
            ),
          })
        : openModal({ size: "lg", aiMode: true, drawHeaderBorder: true, content: <GenerateTakeoffModal /> }),
  };
  const actions: HeaderAction[] = [
    ...(designerModifiedSlotsEnabled && pendingSlotCount > 0 ? [reviewSlotsAction] : []),
    // Not gated on the `PlanPackagesTakeoffAi` feature flag since Generate Takeoffs supports both states:
    // - On: creates AEPs for review
    // - Off: directly creates/updates TLIs
    ...(planPackagesAiEnabled ? [generateTakeoffsAction] : []),
    cycleTrackingAction({
      title: "Plan Cycle",
      startedAt: takeoffsStartedAt?.toPlainDate(),
      completedAt: takeoffsCompletedAt?.toPlainDate(),
      onStart: () => saveCycle({ takeoffsStartedAt: nowUTC() }),
      onMarkComplete: () => saveCycle({ takeoffsCompletedAt: nowUTC() }),
      onReopen: () => saveCycle({ takeoffsCompletedAt: null }),
      onReset: () => saveCycle({ takeoffsStartedAt: null, takeoffsCompletedAt: null }),
    }),
    createAddMenu(canEditLineItems, importItems, openAdd),
  ];

  return <ContentHeader title="Takeoffs" actions={actions} />;
}

/**
 * Lines are generated from the plan's data, so unreviewed suggestions to it block generation. Returns the first tab,
 * in page order, still holding some.
 */
function pendingSuggestionsRoute(aiProposal: PlanPackagePageV2_PlanPackageFragment["aiProposal"]) {
  if (aiProposal?.hasElevationProposals) return planPackageElevationsRoute;
  if (aiProposal?.hasProgramDataProposals) return planPackageProgramDataRoute;
  if (aiProposal?.hasLocationProposals) return planPackageLocationsRoute;
  if (aiProposal?.hasOptionProposals) return planPackageOptionsRoute;
  return undefined;
}

/**
 * A plan package holds one open proposal at a time, so one still processing, or holding only takeoff suggestions,
 * blocks generation too. A failed one is left for the backend to clear.
 */
function openProposalReason(aiProposal: PlanPackagePageV2_PlanPackageFragment["aiProposal"]) {
  switch (aiProposal?.status.code) {
    case AiProposalStatus.Pending:
      return "We're still processing your document";
    case AiProposalStatus.Reviewing:
      return "Accept or reject the AI takeoff suggestions first";
    default:
      return undefined;
  }
}

/** The Takeoffs header's Add menu. */
function createAddMenu(
  canEditLineItems: PotentialOperation2,
  importItems: VoidFunction,
  openAdd: (type: LineType) => void,
): HeaderAction {
  return {
    kind: "menu",
    trigger: { label: "Add" },
    disabled: disableBasedOnPotentialOperation(canEditLineItems),
    items: [...addLineMenuItems(openAdd), { label: "Import Items", icon: "upload", onClick: importItems }],
  };
}
